import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Radio, Mail, MessageSquare, MessageCircle, RefreshCw, Send, ShieldCheck,
  AlertTriangle, CheckCircle2, XCircle, Activity, Filter, PauseCircle, PlayCircle,
} from "lucide-react";

const CHANNELS = [
  { key: "email",    label: "Email",     icon: Mail },
  { key: "sms",      label: "SMS",       icon: MessageSquare },
  { key: "whatsapp", label: "WhatsApp",  icon: MessageCircle },
];

const HEALTH_TONE = {
  healthy:      "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  degraded:     "bg-amber-500/10 text-amber-700 border-amber-500/20",
  unhealthy:    "bg-red-500/10 text-red-700 border-red-500/20",
  unconfigured: "bg-slate-500/10 text-slate-600 border-slate-500/20",
  idle:         "bg-slate-500/10 text-slate-600 border-slate-500/20",
};

const HQ_ROLES = new Set(["super_admin", "admin"]);
const EDIT_ROUTING_ROLES = new Set(["super_admin", "admin", "country_admin"]);
const TEST_SEND_ROLES = new Set(["super_admin", "admin", "country_admin"]);

/**
 * Admin · Notification Operations Centre — /admin/notifications
 *
 * Provider health, per-country routing overrides, delivery logs, queue
 * depth, manual test send. Strict RBAC — country_admin only sees own
 * country. No raw credentials ever surfaced (boolean `configured` only).
 */
export default function NotificationOps() {
  const { user } = useAuth();
  const canEditRouting = user && EDIT_ROUTING_ROLES.has(user.role);
  const canTestSend    = user && TEST_SEND_ROLES.has(user.role);
  const isHQ           = user && HQ_ROLES.has(user.role);

  const [providers, setProviders]   = useState(null);
  const [routing, setRouting]       = useState(null);
  const [queue, setQueue]           = useState(null);
  const [logs, setLogs]             = useState({ items: [], count: 0 });
  const [busy, setBusy]             = useState(false);
  const [logFilter, setLogFilter]   = useState({ channel: "", status: "", provider: "", country: "" });
  const [routingDraft, setRoutingDraft] = useState({}); // iso → { channel: { provider, fallback } }
  const [testOpen, setTestOpen]     = useState(false);
  const [testCtx, setTestCtx]       = useState(null);   // { channel, providerKey }
  const [testForm, setTestForm]     = useState({ to: "", note: "", country_iso: "" });
  const [confirmTest, setConfirmTest] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [p, r, q, l] = await Promise.all([
        api.get("/notifications/providers"),
        api.get("/notifications/routing"),
        api.get("/notifications/queue?hours=24"),
        api.get("/notifications/logs?limit=50"),
      ]);
      setProviders(p.data);
      setRouting(r.data);
      setQueue(q.data);
      setLogs(l.data);
      setRoutingDraft({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Notification Ops");
    } finally { setBusy(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const applyLogFilter = async () => {
    const params = new URLSearchParams();
    if (logFilter.channel)  params.set("channel",  logFilter.channel);
    if (logFilter.status)   params.set("status",   logFilter.status);
    if (logFilter.provider) params.set("provider", logFilter.provider);
    if (logFilter.country)  params.set("country",  logFilter.country);
    params.set("limit", "100");
    try {
      const r = await api.get(`/notifications/logs?${params.toString()}`);
      setLogs(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleProvider = async (key, currentlyDisabled) => {
    if (!isHQ) return toast.error("Only Super Admin can disable providers");
    try {
      await api.post(`/notifications/providers/${key}/toggle`, {
        disabled: !currentlyDisabled,
        reason: currentlyDisabled ? "Operator re-enable" : "Operator disable",
      });
      toast.success(`${key} · ${currentlyDisabled ? "enabled" : "disabled"}`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const setDraftFor = (iso, channel, patch) => {
    setRoutingDraft(prev => ({
      ...prev,
      [iso]: { ...(prev[iso] || {}), [channel]: { ...((prev[iso] || {})[channel] || {}), ...patch } },
    }));
  };

  const saveRouting = async (iso) => {
    const draft = routingDraft[iso];
    if (!draft) return;
    // build payload only for touched channels
    const payload = {};
    for (const ch of Object.keys(draft)) {
      payload[ch] = {
        provider: (draft[ch].provider || "") || null,
        fallback: (draft[ch].fallback || "") || null,
      };
    }
    try {
      await api.put(`/notifications/routing/${iso}`, payload);
      toast.success(`${iso} · routing saved`);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openTest = (channel, providerKey) => {
    setTestCtx({ channel, providerKey });
    setTestForm({ to: "", note: "", country_iso: user?.country || "" });
    setConfirmTest(false);
    setTestOpen(true);
  };

  const doTestSend = async () => {
    if (!testCtx) return;
    if (!confirmTest) { toast.error("Please confirm the test send"); return; }
    try {
      const r = await api.post(`/notifications/providers/${testCtx.providerKey}/test`, {
        channel: testCtx.channel, to: testForm.to,
        country_iso: testForm.country_iso || null,
        note: testForm.note || null,
      });
      const s = r.data.status;
      const tone = s === "sent" ? toast.success : (s === "stub" ? toast.info : toast.error);
      tone(`Test ${testCtx.channel.toUpperCase()} · ${testCtx.providerKey} → ${s}${r.data.error ? " · " + r.data.error : ""}`);
      setTestOpen(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
  };

  const providerChoices = useMemo(() => {
    const p = providers?.providers || {};
    return {
      email:    (p.email    || []).map(x => ({ key: x.key, label: x.label, configured: x.configured, disabled: x.disabled })),
      sms:      (p.sms      || []).map(x => ({ key: x.key, label: x.label, configured: x.configured, disabled: x.disabled })),
      whatsapp: (p.whatsapp || []).map(x => ({ key: x.key, label: x.label, configured: x.configured, disabled: x.disabled })),
    };
  }, [providers]);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="kicker">Executive · Notification Operations Centre</div>
            <h1 className="display text-4xl">Communications Command &amp; Control</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Provider health, per-country routing, delivery queue, retry telemetry and audited
              test-sends across Email, SMS and WhatsApp. Every action is captured to the immutable
              ledger (actor · IP · timestamp · prev · new). Raw credentials never surface here.
            </p>
            {user && (
              <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mt-2">
                Signed in as <span className="text-primary">{user.role}</span>
                {user.country ? ` · country ${user.country}` : " · Africa HQ (all countries)"}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button variant="outline" size="sm" onClick={loadAll} disabled={busy} data-testid="notif-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <Link to="/ops"><Button variant="outline" size="sm">Ops Centre</Button></Link>
            <Link to="/admin/countries"><Button variant="outline" size="sm">Country Config</Button></Link>
          </div>
        </header>

        {/* Channel health strip */}
        <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="notif-health-strip">
          {CHANNELS.map(({ key, label, icon: Icon }) => {
            const h = queue?.health?.[key] || { state: "idle", success_pct: null };
            const stats = queue?.channels?.[key] || { total: 0, sent: 0, failed: 0, stub: 0 };
            return (
              <div key={key} className={`border p-5 bg-card ${HEALTH_TONE[h.state] || HEALTH_TONE.idle}`} data-testid={`notif-health-${key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <div className="font-semibold text-sm">{label}</div>
                  </div>
                  <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-white/60">
                    {h.state}
                  </span>
                </div>
                <div className="mt-4 flex items-end gap-2">
                  <div className="text-3xl font-semibold leading-none">{h.success_pct == null ? "—" : `${h.success_pct}%`}</div>
                  <div className="text-[11px] opacity-80 pb-1">delivery · last 24h</div>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-4 text-[11px]">
                  <Chip l="Sent"   v={stats.sent} tone="emerald" />
                  <Chip l="Failed" v={stats.failed} tone="red" />
                  <Chip l="Stub"   v={stats.stub} tone="slate" />
                  <Chip l="Total"  v={stats.total} tone="primary" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Providers */}
        <section className="mb-10" data-testid="notif-providers">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-semibold">Providers</h2>
            </div>
            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">
              Rate limiter: <span className="text-primary">{providers?.infrastructure?.rate_limiter || "—"}</span>
              {" · "}Malware scan: <span className="text-primary">{providers?.infrastructure?.malware_scanner || "—"}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {CHANNELS.map(({ key: ch, label, icon: Icon }) => {
              const list = providerChoices[ch] || [];
              const active = providers?.defaults?.[ch];
              return (
                <div key={ch} className="border border-border bg-card" data-testid={`notif-provider-block-${ch}`}>
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                      <div className="text-sm font-medium">{label}</div>
                    </div>
                    <span className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">
                      default: {active}
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {list.map(p => {
                      const isActive  = p.key === active;
                      const stateTone = p.disabled
                        ? "bg-red-500/10 text-red-700"
                        : (p.configured ? "bg-emerald-500/10 text-emerald-700" : "bg-slate-500/10 text-slate-600");
                      return (
                        <div key={p.key} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`notif-provider-${p.key}`}>
                          <div className="min-w-0">
                            <div className="text-[13px] font-medium truncate flex items-center gap-2">
                              {p.label}
                              {isActive && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">active</span>}
                            </div>
                            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mt-0.5">
                              key: <span>{p.key}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${stateTone}`}>
                              {p.disabled ? "disabled" : (p.configured ? "configured" : "unconfigured")}
                            </span>
                            {canTestSend && (
                              <Button variant="outline" size="sm" onClick={() => openTest(ch, p.key)} data-testid={`notif-test-${p.key}`}>
                                <Send className="h-3 w-3 mr-1" /> Test
                              </Button>
                            )}
                            {isHQ && (
                              <Button variant="ghost" size="sm"
                                title={p.disabled ? "Re-enable provider" : "Disable provider"}
                                onClick={() => toggleProvider(p.key, p.disabled)}
                                data-testid={`notif-toggle-${p.key}`}>
                                {p.disabled ? <PlayCircle className="h-4 w-4 text-emerald-600" /> : <PauseCircle className="h-4 w-4 text-red-600" />}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {list.length === 0 && <div className="p-4 text-xs text-muted-foreground">No providers registered.</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Country routing matrix */}
        <section className="mb-10" data-testid="notif-routing">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-semibold">Per-Country Routing</h2>
            </div>
            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">
              {canEditRouting ? "editable" : "read-only"} · fallback triggers on primary failure
            </div>
          </div>

          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="p-3 font-medium">Country</th>
                  {CHANNELS.map(({ key, label }) => (
                    <th key={key} className="p-3 font-medium">{label} · primary / fallback</th>
                  ))}
                  {canEditRouting && <th className="p-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(routing?.items || []).map(row => {
                  const draft = routingDraft[row.iso] || {};
                  const touched = Object.keys(draft).length > 0;
                  return (
                    <tr key={row.iso} data-testid={`notif-routing-${row.iso}`}>
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-medium">{row.name}</div>
                        <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">{row.iso}</div>
                      </td>
                      {CHANNELS.map(({ key: ch }) => {
                        const current = row.routing?.[ch] || {};
                        const dCh = draft[ch] || {};
                        const primary  = dCh.provider ?? current.provider ?? "";
                        const fallback = dCh.fallback ?? current.fallback ?? "";
                        return (
                          <td key={ch} className="p-2 min-w-[220px]">
                            <div className="flex flex-col gap-1">
                              <Select
                                disabled={!canEditRouting}
                                value={primary}
                                choices={providerChoices[ch]}
                                onChange={(v) => setDraftFor(row.iso, ch, { provider: v })}
                                data-testid={`notif-primary-${row.iso}-${ch}`}
                              />
                              <Select
                                disabled={!canEditRouting}
                                value={fallback}
                                choices={providerChoices[ch]}
                                onChange={(v) => setDraftFor(row.iso, ch, { fallback: v })}
                                placeholder="— no fallback —"
                                data-testid={`notif-fallback-${row.iso}-${ch}`}
                              />
                              {current.overridden && (
                                <span className="mono text-[9.5px] uppercase tracking-widest text-amber-700">override active</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      {canEditRouting && (
                        <td className="p-3">
                          <Button size="sm" disabled={!touched} onClick={() => saveRouting(row.iso)} data-testid={`notif-save-${row.iso}`}>
                            Save
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {(!routing || routing.items?.length === 0) && (
                  <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">No countries in scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Delivery Logs */}
        <section data-testid="notif-logs">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              <h2 className="text-lg font-semibold">Delivery Log</h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <Select choices={[{ key: "", label: "All channels" }, ...CHANNELS.map(c => ({ key: c.key, label: c.label }))]}
                value={logFilter.channel} onChange={v => setLogFilter(f => ({ ...f, channel: v }))} />
              <Select choices={[{ key: "", label: "All statuses" },
                                { key: "sent", label: "sent" }, { key: "failed", label: "failed" },
                                { key: "stub", label: "stub" }, { key: "queued", label: "queued" }]}
                value={logFilter.status} onChange={v => setLogFilter(f => ({ ...f, status: v }))} />
              <Input placeholder="provider key" value={logFilter.provider}
                     onChange={(e) => setLogFilter(f => ({ ...f, provider: e.target.value }))}
                     className="h-8 w-32 text-[12px]" data-testid="notif-log-filter-provider" />
              {isHQ && (
                <Input placeholder="country ISO" value={logFilter.country}
                       onChange={(e) => setLogFilter(f => ({ ...f, country: e.target.value.toUpperCase().slice(0,2) }))}
                       className="h-8 w-24 text-[12px]" data-testid="notif-log-filter-country" />
              )}
              <Button size="sm" onClick={applyLogFilter} data-testid="notif-log-apply">Apply</Button>
            </div>
          </div>

          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="p-2.5 font-medium">Time</th>
                  <th className="p-2.5 font-medium">Channel</th>
                  <th className="p-2.5 font-medium">Provider</th>
                  <th className="p-2.5 font-medium">Recipient</th>
                  <th className="p-2.5 font-medium">Category</th>
                  <th className="p-2.5 font-medium">Country</th>
                  <th className="p-2.5 font-medium">Status</th>
                  <th className="p-2.5 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(logs.items || []).map((r, i) => {
                  const tone = r.status === "sent" ? "text-emerald-700"
                             : r.status === "failed" ? "text-red-700"
                             : r.status === "stub"   ? "text-amber-700" : "text-muted-foreground";
                  const StatusIcon = r.status === "sent" ? CheckCircle2
                                  : r.status === "failed" ? XCircle
                                  : r.status === "stub"   ? AlertTriangle : Activity;
                  return (
                    <tr key={i} data-testid={`notif-log-row-${i}`}>
                      <td className="p-2.5 whitespace-nowrap mono text-[10.5px] text-muted-foreground">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="p-2.5 uppercase mono text-[10.5px]">{r.channel}</td>
                      <td className="p-2.5">{r.provider}</td>
                      <td className="p-2.5 mono text-[11.5px] max-w-[220px] truncate" title={r.to}>{maskRecipient(r.to)}</td>
                      <td className="p-2.5 mono text-[10.5px] uppercase text-muted-foreground">{r.category || "—"}</td>
                      <td className="p-2.5 mono text-[10.5px]">{r.meta?.country_iso || "—"}</td>
                      <td className={`p-2.5 uppercase mono text-[10.5px] ${tone}`}>
                        <span className="inline-flex items-center gap-1"><StatusIcon className="h-3 w-3" />{r.status}</span>
                      </td>
                      <td className="p-2.5 text-[11.5px] text-muted-foreground max-w-[300px] truncate" title={r.error || r.provider_id || ""}>
                        {r.error || r.provider_id || r.subject || (r.meta?.body || "").slice(0, 80)}
                      </td>
                    </tr>
                  );
                })}
                {(logs.items || []).length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">No delivery activity in scope.</td></tr>
                )}
              </tbody>
            </table>
            <div className="p-3 flex items-center justify-between text-[11px] text-muted-foreground border-t border-border">
              <span>Showing {logs.items?.length || 0} of {logs.count || 0} messages</span>
              <span>Recipient contact masked for privacy · full value visible only to the sending workflow's audit log.</span>
            </div>
          </div>
        </section>
      </div>

      {/* Test send dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md" data-testid="notif-test-dialog">
          <DialogHeader>
            <DialogTitle>Send test · {testCtx?.channel?.toUpperCase()} · {testCtx?.providerKey}</DialogTitle>
            <DialogDescription>
              Dispatches a real message via the selected provider. The outcome and this operator action are recorded to the immutable audit ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recipient</div>
              <Input
                placeholder={testCtx?.channel === "email" ? "ops@example.com" : "+2637XXXXXXXX"}
                value={testForm.to}
                onChange={(e) => setTestForm({ ...testForm, to: e.target.value })}
                data-testid="notif-test-to"
              />
            </div>
            {isHQ && (
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Country ISO (optional)</div>
                <Input
                  placeholder="ZW / KE / NG …"
                  value={testForm.country_iso}
                  onChange={(e) => setTestForm({ ...testForm, country_iso: e.target.value.toUpperCase().slice(0,2) })}
                  data-testid="notif-test-iso"
                />
              </div>
            )}
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Note (audit trail)</div>
              <Textarea rows={2} value={testForm.note}
                onChange={(e) => setTestForm({ ...testForm, note: e.target.value })}
                placeholder="e.g. Post-outage validation" data-testid="notif-test-note" />
            </div>
            <label className="flex items-start gap-2 text-[12px] text-muted-foreground">
              <input type="checkbox" checked={confirmTest} onChange={(e) => setConfirmTest(e.target.checked)} data-testid="notif-test-confirm" />
              <span>
                I confirm this dispatches a real message via <strong>{testCtx?.providerKey}</strong> and will be recorded to the
                immutable audit ledger (actor, IP, timestamp, provider outcome).
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={doTestSend} disabled={!testForm.to || !confirmTest} data-testid="notif-test-send">
              <Send className="h-3.5 w-3.5 mr-1" /> Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const Chip = ({ l, v, tone }) => {
  const toneMap = {
    emerald: "bg-emerald-500/10 text-emerald-700",
    red:     "bg-red-500/10 text-red-700",
    slate:   "bg-slate-500/10 text-slate-700",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <div className={`px-2 py-1 rounded-sm ${toneMap[tone]} flex flex-col`}>
      <span className="mono text-[9.5px] uppercase tracking-widest opacity-80">{l}</span>
      <span className="font-semibold text-[13px] leading-tight">{v ?? 0}</span>
    </div>
  );
};

const Select = ({ choices, value, onChange, disabled, placeholder, ...rest }) => (
  <select
    disabled={disabled}
    value={value || ""}
    onChange={(e) => onChange?.(e.target.value)}
    className="h-8 w-full border border-border rounded-sm bg-background text-[12px] px-2 disabled:opacity-70"
    {...rest}
  >
    <option value="">{placeholder || "— platform default —"}</option>
    {(choices || []).map(c => (
      <option key={c.key} value={c.key}>
        {c.label}{c.configured === false ? " · unconfigured" : ""}{c.disabled ? " · disabled" : ""}
      </option>
    ))}
  </select>
);

const maskRecipient = (v) => {
  if (!v) return "—";
  if (v.includes("@")) {
    const [u, d] = v.split("@");
    if (u.length <= 2) return `${u[0] || "•"}***@${d}`;
    return `${u.slice(0, 2)}***@${d}`;
  }
  if (v.length > 6) return `${v.slice(0, 4)}•••${v.slice(-2)}`;
  return "•••";
};
